import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Package, Plus, Edit, Trash, User, MapPin, X, Clock, Printer, Truck } from "lucide-react";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import LocationSelector from "@/components/LocationSelector";
import { printDeliveryLabel } from "@/lib/printLabel";
import { toast } from "sonner";
import axios from "axios";
import { useAuth } from "@/context/AuthContext";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function VendorDashboard() {
  const navigate = useNavigate();
  const { token, user, loading: authLoading, updateUserProfile } = useAuth();
  const [products, setProducts] = useState([]);
  const [vendorOrders, setVendorOrders] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAddProduct, setShowAddProduct] = useState(false);
  const [editingProduct, setEditingProduct] = useState(null);
  const [editingProfile, setEditingProfile] = useState(false);
  const [productForm, setProductForm] = useState({
    name: "",
    description: "",
    actual_price: "",
    offer_price: "",
    stock: "",
    category_id: "",
    images: [""],
    seo_title: "",
    seo_description: "",
  });
  const [uploadingImage, setUploadingImage] = useState(false);
  const [imageUrlInput, setImageUrlInput] = useState("");
  const [cities, setCities] = useState([]);
  const [uploadingQr, setUploadingQr] = useState(false);
  const [profileData, setProfileData] = useState({
    name: user?.name || "",
    email: user?.email || "",
    location: user?.location || { district: "", city: "", pincode: "", state: "Tamil Nadu" },
    delivery_cities: user?.delivery_cities || [],
    has_delivery: user?.has_delivery !== undefined ? user.has_delivery : true,
    payment_details: user?.payment_details || { gpay: "", qr_image: "", bank_account: "", account_name: "", ifsc: "" },
  });

  useEffect(() => {
    // Wait for auth loading to complete
    if (authLoading) return;
    
    if (!user || (user.role !== "vendor" && user.role !== "admin")) {
      toast.error("Vendor access required");
      navigate("/");
      return;
    }
    fetchData();
  }, [user, authLoading]);

  useEffect(() => {
    axios.get(`${API}/cities`).then((r) => setCities(r.data)).catch(() => {});
  }, []);

  useEffect(() => {
    if (user) {
      setProfileData({
        name: user.name || "",
        email: user.email || "",
        location: user.location || { district: "", city: "", pincode: "", state: "Tamil Nadu" },
        delivery_cities: user.delivery_cities || [],
        has_delivery: user.has_delivery !== undefined ? user.has_delivery : true,
        payment_details: user.payment_details || { gpay: "", qr_image: "", bank_account: "", account_name: "", ifsc: "" },
      });
    }
  }, [user]);

  const fetchData = async () => {
    try {
      const [productsRes, ordersRes, categoriesRes] = await Promise.all([
        axios.get(`${API}/vendor/products`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/vendor/orders`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/categories`),
      ]);
      setProducts(productsRes.data);
      setVendorOrders(ordersRes.data);
      setCategories(categoriesRes.data);
    } catch (error) {
      console.error("Failed to fetch data:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleAddProduct = async (e) => {
    e.preventDefault();
    try {
      const productData = {
        ...productForm,
        actual_price: parseFloat(productForm.actual_price),
        offer_price: parseFloat(productForm.offer_price),
        stock: parseInt(productForm.stock),
        images: productForm.images.filter((img) => img.trim() !== ""),
      };

      if (editingProduct) {
        await axios.put(`${API}/products/${editingProduct.id}`, productData, {
          headers: { Authorization: `Bearer ${token}` },
        });
        toast.success("Product updated successfully");
      } else {
        await axios.post(`${API}/products`, productData, {
          headers: { Authorization: `Bearer ${token}` },
        });
        toast.success("Product added successfully");
      }
      setShowAddProduct(false);
      setEditingProduct(null);
      setProductForm({ name: "", description: "", actual_price: "", offer_price: "", stock: "", category_id: "", images: [""], seo_title: "", seo_description: "" });
      fetchData();
    } catch (error) {
      toast.error(editingProduct ? "Failed to update product" : "Failed to add product");
    }
  };

  const handleImageUpload = async (e) => {
    const files = Array.from(e.target.files || []);
    if (files.length === 0) return;

    setUploadingImage(true);
    try {
      const uploaded = [];
      for (const file of files) {
        const formData = new FormData();
        formData.append("file", file);
        const response = await axios.post(`${API}/upload-image`, formData, {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "multipart/form-data",
          },
        });
        uploaded.push(`${process.env.REACT_APP_BACKEND_URL}${response.data.url}`);
      }
      setProductForm((prev) => ({
        ...prev,
        images: [...prev.images.filter((img) => img.trim() !== ""), ...uploaded],
      }));
      toast.success(`${uploaded.length} image(s) uploaded successfully!`);
    } catch (error) {
      toast.error("Failed to upload image");
    } finally {
      setUploadingImage(false);
      e.target.value = "";
    }
  };

  const removeImage = (idx) => {
    setProductForm((prev) => ({
      ...prev,
      images: prev.images.filter((_, i) => i !== idx),
    }));
  };

  const addImageUrl = () => {
    const url = imageUrlInput.trim();
    if (!url) return;
    setProductForm((prev) => ({
      ...prev,
      images: [...prev.images.filter((img) => img.trim() !== ""), url],
    }));
    setImageUrlInput("");
  };

  const calculateDiscount = () => {
    const actual = parseFloat(productForm.actual_price) || 0;
    const offer = parseFloat(productForm.offer_price) || 0;
    if (actual > 0 && offer < actual) {
      return ((actual - offer) / actual * 100).toFixed(1);
    }
    return 0;
  };

  const handleEditProduct = (product) => {
    setEditingProduct(product);
    setProductForm({
      name: product.name,
      description: product.description,
      actual_price: product.actual_price?.toString() || product.price?.toString() || "",
      offer_price: product.offer_price?.toString() || product.price?.toString() || "",
      stock: product.stock.toString(),
      category_id: product.category_id,
      images: product.images.length > 0 ? product.images : [""],
      seo_title: product.seo_title || "",
      seo_description: product.seo_description || "",
    });
    setShowAddProduct(true);
  };

  const handleDeleteProduct = async (productId) => {
    if (!window.confirm("Are you sure you want to delete this product?")) {
      return;
    }
    try {
      await axios.delete(`${API}/products/${productId}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      toast.success("Product deleted successfully");
      fetchData();
    } catch (error) {
      toast.error("Failed to delete product");
    }
  };

  const handleCancelEdit = () => {
    setShowAddProduct(false);
    setEditingProduct(null);
    setProductForm({ name: "", description: "", actual_price: "", offer_price: "", stock: "", category_id: "", images: [""] });
  };

  const handleUpdateProfile = async (e) => {
    e.preventDefault();
    try {
      await updateUserProfile({
        name: profileData.name,
        email: profileData.email,
        location: profileData.location,
        delivery_cities: profileData.has_delivery ? profileData.delivery_cities : [],
        has_delivery: profileData.has_delivery,
        payment_details: profileData.payment_details,
      });
      toast.success("Profile updated successfully");
      setEditingProfile(false);
    } catch (error) {
      toast.error("Failed to update profile");
    }
  };

  const toggleDeliveryCity = (cityName) => {
    setProfileData((p) => ({
      ...p,
      delivery_cities: p.delivery_cities.includes(cityName)
        ? p.delivery_cities.filter((c) => c !== cityName)
        : [...p.delivery_cities, cityName],
    }));
  };

  const handleQrUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setUploadingQr(true);
    try {
      const formData = new FormData();
      formData.append("file", file);
      const res = await axios.post(`${API}/upload-image`, formData, {
        headers: { Authorization: `Bearer ${token}`, "Content-Type": "multipart/form-data" },
      });
      const url = `${process.env.REACT_APP_BACKEND_URL}${res.data.url}`;
      setProfileData((p) => ({ ...p, payment_details: { ...p.payment_details, qr_image: url } }));
      toast.success("QR code uploaded");
    } catch (error) {
      toast.error("Failed to upload QR code");
    } finally {
      setUploadingQr(false);
      e.target.value = "";
    }
  };

  const handleUpdateVendorOrderStatus = async (vendorOrderId, status) => {
    try {
      await axios.put(
        `${API}/vendor/orders/${vendorOrderId}/status`,
        { status },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success(`Order marked ${status}`);
      fetchData();
    } catch (error) {
      toast.error("Failed to update order status");
    }
  };

  const handlePrintLabel = (order) => {
    printDeliveryLabel({
      orderId: order.order_id,
      customerName: order.customer?.name,
      customerPhone: order.customer?.phone,
      address: order.shipping_address,
      items: order.items,
      vendorName: user?.name,
    });
  };

  if (loading) {
    return (
      <div data-testid="vendor-dashboard-loading">
        <Navbar />
        <div className="min-h-screen flex items-center justify-center">
          <p className="text-muted-foreground">Loading...</p>
        </div>
      </div>
    );
  }

  return (
    <div data-testid="vendor-dashboard">
      <Navbar />

      <div className="min-h-screen py-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {user?.role === "vendor" && user?.vendor_status === "pending" && (
            <div className="bg-yellow-50 border border-yellow-300 rounded-2xl p-5 mb-8 flex items-start gap-3" data-testid="vendor-pending-banner">
              <Clock className="w-6 h-6 text-yellow-600 flex-shrink-0 mt-0.5" />
              <div>
                <p className="font-semibold text-yellow-800">Your vendor account is pending approval</p>
                <p className="text-sm text-yellow-700 mt-1">
                  You can add products now, but they won't appear in the public store until an admin approves your account.
                </p>
              </div>
            </div>
          )}
          <div className="flex justify-between items-center mb-8">
            <h1 className="font-heading text-4xl md:text-5xl font-bold text-primary" data-testid="vendor-dashboard-title">
              Vendor Dashboard
            </h1>
            <Button
              onClick={() => setShowAddProduct(!showAddProduct)}
              className="bg-accent text-accent-foreground hover:bg-accent/90 rounded-full"
              data-testid="vendor-add-product-btn"
            >
              <Plus className="w-5 h-5 mr-2" />
              Add Product
            </Button>
          </div>

          {/* Vendor Profile Card */}
          <div className="bg-card rounded-2xl p-6 shadow-soft mb-8" data-testid="vendor-profile-card">
            <div className="flex items-center space-x-3 mb-6">
              <User className="w-6 h-6 text-primary" />
              <h2 className="font-heading text-2xl font-bold">Vendor Profile</h2>
            </div>

            {editingProfile ? (
              <form onSubmit={handleUpdateProfile} className="space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium mb-2">Name</label>
                    <Input
                      value={profileData.name}
                      onChange={(e) => setProfileData({ ...profileData, name: e.target.value })}
                      data-testid="vendor-profile-name-input"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-2">Email</label>
                    <Input
                      type="email"
                      value={profileData.email}
                      onChange={(e) => setProfileData({ ...profileData, email: e.target.value })}
                      data-testid="vendor-profile-email-input"
                    />
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-medium mb-2">Phone</label>
                  <Input value={user.phone} disabled />
                </div>
                
                {/* Location Selector - REQUIRED for vendors */}
                <div className="border-t border-border pt-4 mt-4">
                  <LocationSelector
                    location={profileData.location}
                    onChange={(location) => setProfileData({ ...profileData, location })}
                    required={true}
                  />
                  <p className="text-sm text-muted-foreground mt-2">
                    Your store address (used for pickup / shipping).
                  </p>
                </div>

                {/* Delivery Cities */}
                <div className="border-t border-border pt-4 mt-4">
                  <label className="block text-sm font-medium mb-2">Delivery Cities</label>
                  <label className="flex items-center gap-2 mb-3 text-sm cursor-pointer">
                    <input
                      type="checkbox"
                      checked={!profileData.has_delivery}
                      onChange={(e) => setProfileData({ ...profileData, has_delivery: !e.target.checked })}
                      data-testid="vendor-no-delivery-toggle"
                    />
                    I don't have a delivery option
                  </label>
                  {profileData.has_delivery && (
                    <div className="flex flex-wrap gap-2" data-testid="vendor-delivery-cities">
                      {cities.map((c) => {
                        const selected = profileData.delivery_cities.includes(c.name);
                        return (
                          <button
                            type="button"
                            key={c.id}
                            onClick={() => toggleDeliveryCity(c.name)}
                            className={`px-4 py-1.5 rounded-full text-sm border transition-colors ${
                              selected
                                ? "bg-primary text-primary-foreground border-primary"
                                : "bg-background border-border hover:border-primary"
                            }`}
                            data-testid={`vendor-delivery-city-${c.name}`}
                          >
                            {c.name}
                          </button>
                        );
                      })}
                      {cities.length === 0 && (
                        <p className="text-sm text-muted-foreground">No cities configured yet.</p>
                      )}
                    </div>
                  )}
                </div>

                {/* Payment Settlement Details (optional) */}
                <div className="border-t border-border pt-4 mt-4">
                  <label className="block text-sm font-medium mb-1">Payment Settlement Details</label>
                  <p className="text-xs text-muted-foreground mb-3">
                    Where you'd like to receive payouts. All fields are optional.
                  </p>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm mb-1">GPay / UPI Number</label>
                      <Input
                        value={profileData.payment_details.gpay}
                        onChange={(e) => setProfileData({ ...profileData, payment_details: { ...profileData.payment_details, gpay: e.target.value } })}
                        placeholder="e.g. 9884436893"
                        data-testid="vendor-gpay-input"
                      />
                    </div>
                    <div>
                      <label className="block text-sm mb-1">Account Holder Name</label>
                      <Input
                        value={profileData.payment_details.account_name}
                        onChange={(e) => setProfileData({ ...profileData, payment_details: { ...profileData.payment_details, account_name: e.target.value } })}
                        data-testid="vendor-account-name-input"
                      />
                    </div>
                    <div>
                      <label className="block text-sm mb-1">Bank Account Number</label>
                      <Input
                        value={profileData.payment_details.bank_account}
                        onChange={(e) => setProfileData({ ...profileData, payment_details: { ...profileData.payment_details, bank_account: e.target.value } })}
                        data-testid="vendor-bank-account-input"
                      />
                    </div>
                    <div>
                      <label className="block text-sm mb-1">IFSC Code</label>
                      <Input
                        value={profileData.payment_details.ifsc}
                        onChange={(e) => setProfileData({ ...profileData, payment_details: { ...profileData.payment_details, ifsc: e.target.value } })}
                        data-testid="vendor-ifsc-input"
                      />
                    </div>
                  </div>
                  <div className="mt-4">
                    <label className="block text-sm mb-1">UPI QR Code</label>
                    <input type="file" accept="image/*" onChange={handleQrUpload} className="hidden" id="qr-upload" />
                    <label
                      htmlFor="qr-upload"
                      className="inline-block px-4 py-2 bg-primary text-primary-foreground rounded-lg cursor-pointer hover:bg-primary/90"
                      data-testid="vendor-qr-upload-btn"
                    >
                      {uploadingQr ? "Uploading..." : "Upload QR"}
                    </label>
                    {profileData.payment_details.qr_image && (
                      <img
                        src={profileData.payment_details.qr_image}
                        alt="Payment QR"
                        className="w-24 h-24 object-cover rounded-lg mt-2 border border-border"
                        data-testid="vendor-qr-preview"
                      />
                    )}
                  </div>
                </div>
                
                <div className="flex space-x-2">
                  <Button type="submit" className="flex-1 rounded-full" data-testid="vendor-profile-save-btn">
                    Save Profile
                  </Button>
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => setEditingProfile(false)}
                    className="flex-1 rounded-full"
                    data-testid="vendor-profile-cancel-btn"
                  >
                    Cancel
                  </Button>
                </div>
              </form>
            ) : (
              <div className="space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div>
                    <p className="text-sm text-muted-foreground mb-1">Name</p>
                    <p className="font-semibold">{user.name || "Not set"}</p>
                  </div>
                  <div>
                    <p className="text-sm text-muted-foreground mb-1">Email</p>
                    <p className="font-semibold">{user.email || "Not set"}</p>
                  </div>
                  <div>
                    <p className="text-sm text-muted-foreground mb-1">Phone</p>
                    <p className="font-semibold">{user.phone}</p>
                  </div>
                </div>
                
                {user.location && user.location.district ? (
                  <div className="bg-primary/5 rounded-xl p-4 border border-primary/20">
                    <p className="text-sm text-muted-foreground mb-2 flex items-center">
                      <MapPin className="w-4 h-4 mr-1 text-primary" />
                      Your Store Location
                    </p>
                    <p className="font-semibold text-lg">
                      {user.location.city}, {user.location.district}
                    </p>
                    <p className="text-sm text-muted-foreground">
                      {user.location.pincode}, Tamil Nadu
                    </p>
                  </div>
                ) : (
                  <div className="bg-yellow-50 border border-yellow-200 rounded-xl p-4">
                    <p className="text-yellow-800 font-medium mb-1">⚠️ Location Not Set</p>
                    <p className="text-sm text-yellow-700">
                      Please add your location so customers can find your products!
                    </p>
                  </div>
                )}
                
                <div className="bg-secondary/40 rounded-xl p-4" data-testid="vendor-delivery-summary">
                  <p className="text-sm text-muted-foreground mb-2">Delivery</p>
                  {user.has_delivery === false ? (
                    <p className="font-medium text-foreground">No delivery option (pickup only)</p>
                  ) : user.delivery_cities && user.delivery_cities.length > 0 ? (
                    <div className="flex flex-wrap gap-2">
                      {user.delivery_cities.map((c) => (
                        <span key={c} className="px-3 py-1 bg-primary/10 text-primary rounded-full text-sm">{c}</span>
                      ))}
                    </div>
                  ) : (
                    <p className="text-sm text-yellow-700">No delivery cities selected yet</p>
                  )}
                </div>

                <Button
                  onClick={() => setEditingProfile(true)}
                  variant="outline"
                  className="w-full rounded-full"
                  data-testid="vendor-profile-edit-btn"
                >
                  <Edit className="w-4 h-4 mr-2" />
                  Edit Profile, Delivery & Payout
                </Button>
              </div>
            )}
          </div>

          {/* Add Product Form */}
          {showAddProduct && (
            <div className="bg-card rounded-2xl p-6 shadow-soft mb-8" data-testid="vendor-add-product-form">
              <h2 className="font-heading text-2xl font-bold mb-6">
                {editingProduct ? "Edit Product" : "Add New Product"}
              </h2>
              <form onSubmit={handleAddProduct} className="space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium mb-2">Product Name</label>
                    <Input
                      value={productForm.name}
                      onChange={(e) => setProductForm({ ...productForm, name: e.target.value })}
                      required
                      data-testid="vendor-product-name-input"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-2">Category</label>
                    <select
                      value={productForm.category_id}
                      onChange={(e) => setProductForm({ ...productForm, category_id: e.target.value })}
                      required
                      className="w-full px-4 py-2 rounded-lg border border-input bg-background"
                      data-testid="vendor-product-category-select"
                    >
                      <option value="">Select Category</option>
                      {categories.map((cat) => (
                        <option key={cat.id} value={cat.id}>
                          {cat.name}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-2">Actual Price (₹)</label>
                    <Input
                      type="number"
                      step="0.01"
                      value={productForm.actual_price}
                      onChange={(e) => setProductForm({ ...productForm, actual_price: e.target.value })}
                      required
                      placeholder="Original price"
                      data-testid="vendor-product-actual-price-input"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-2">Offer Price (₹)</label>
                    <Input
                      type="number"
                      step="0.01"
                      value={productForm.offer_price}
                      onChange={(e) => setProductForm({ ...productForm, offer_price: e.target.value })}
                      required
                      placeholder="Selling price"
                      data-testid="vendor-product-offer-price-input"
                    />
                    {calculateDiscount() > 0 && (
                      <p className="text-xs text-green-600 mt-1">
                        💰 {calculateDiscount()}% discount
                      </p>
                    )}
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-2">Stock</label>
                    <Input
                      type="number"
                      value={productForm.stock}
                      onChange={(e) => setProductForm({ ...productForm, stock: e.target.value })}
                      required
                      data-testid="vendor-product-stock-input"
                    />
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-medium mb-2">Description</label>
                  <textarea
                    value={productForm.description}
                    onChange={(e) => setProductForm({ ...productForm, description: e.target.value })}
                    required
                    rows={3}
                    className="w-full px-4 py-2 rounded-lg border border-input bg-background"
                    data-testid="vendor-product-description-input"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-2">Product Images</label>
                  <input
                    type="file"
                    accept="image/*"
                    multiple
                    onChange={handleImageUpload}
                    className="hidden"
                    id="image-upload"
                  />
                  <label
                    htmlFor="image-upload"
                    className="inline-block px-4 py-2 bg-primary text-primary-foreground rounded-lg cursor-pointer hover:bg-primary/90"
                    data-testid="vendor-upload-image-btn"
                  >
                    {uploadingImage ? "Uploading..." : "Upload Images"}
                  </label>

                  {productForm.images.filter((img) => img.trim() !== "").length > 0 && (
                    <div className="grid grid-cols-4 gap-3 mt-3" data-testid="vendor-image-thumbnails">
                      {productForm.images
                        .filter((img) => img.trim() !== "")
                        .map((img, idx) => (
                          <div key={idx} className="relative group">
                            <img
                              src={img}
                              alt={`Product ${idx + 1}`}
                              className="w-full aspect-square object-cover rounded-lg border border-border"
                            />
                            {idx === 0 && (
                              <span className="absolute top-1 left-1 bg-primary text-primary-foreground text-[10px] px-1.5 py-0.5 rounded">
                                Main
                              </span>
                            )}
                            <button
                              type="button"
                              onClick={() => removeImage(idx)}
                              className="absolute top-1 right-1 bg-red-500 text-white rounded-full p-1 opacity-90 hover:opacity-100"
                              data-testid={`vendor-remove-image-${idx}`}
                            >
                              <X className="w-3 h-3" />
                            </button>
                          </div>
                        ))}
                    </div>
                  )}

                  <p className="text-xs text-muted-foreground mt-2">
                    First image is the main image. You can upload multiple, or paste an image URL below.
                  </p>
                  <div className="flex gap-2 mt-2">
                    <Input
                      value={imageUrlInput}
                      onChange={(e) => setImageUrlInput(e.target.value)}
                      placeholder="https://example.com/image.jpg"
                      data-testid="vendor-product-image-input"
                    />
                    <Button type="button" variant="outline" onClick={addImageUrl} data-testid="vendor-add-image-url-btn">
                      Add
                    </Button>
                  </div>
                </div>
                
                {/* SEO Fields */}
                <div className="border-t border-border pt-4 mt-4">
                  <h3 className="font-semibold text-lg mb-4">SEO Settings (Optional)</h3>
                  <div className="space-y-4">
                    <div>
                      <label className="block text-sm font-medium mb-2">SEO Title</label>
                      <Input
                        value={productForm.seo_title}
                        onChange={(e) => setProductForm({ ...productForm, seo_title: e.target.value })}
                        placeholder={`${productForm.name} - Buy Online | OrgShop`}
                        maxLength={60}
                        data-testid="vendor-product-seo-title-input"
                      />
                      <p className="text-xs text-muted-foreground mt-1">
                        {productForm.seo_title.length}/60 characters (Recommended: 50-60)
                      </p>
                    </div>
                    <div>
                      <label className="block text-sm font-medium mb-2">SEO Description</label>
                      <textarea
                        value={productForm.seo_description}
                        onChange={(e) => setProductForm({ ...productForm, seo_description: e.target.value })}
                        placeholder={`Buy ${productForm.name} online. ${productForm.description.substring(0, 100)}...`}
                        maxLength={160}
                        rows={3}
                        className="w-full px-4 py-2 rounded-lg border border-input bg-background"
                        data-testid="vendor-product-seo-description-input"
                      />
                      <p className="text-xs text-muted-foreground mt-1">
                        {productForm.seo_description.length}/160 characters (Recommended: 120-160)
                      </p>
                    </div>
                  </div>
                </div>
                
                <div className="flex space-x-4">
                  <Button type="submit" className="flex-1 rounded-full" data-testid="vendor-product-submit-btn">
                    {editingProduct ? "Update Product" : "Add Product"}
                  </Button>
                  <Button
                    type="button"
                    variant="outline"
                    onClick={handleCancelEdit}
                    className="flex-1 rounded-full"
                    data-testid="vendor-product-cancel-btn"
                  >
                    Cancel
                  </Button>
                </div>
              </form>
            </div>
          )}

          {/* Products List */}
          <div className="bg-card rounded-2xl p-6 shadow-soft mb-8" data-testid="vendor-products-section">
            <h2 className="font-heading text-2xl font-bold mb-6">My Products</h2>
            {products.length === 0 ? (
              <div className="text-center py-12" data-testid="vendor-products-empty">
                <Package className="w-16 h-16 text-muted-foreground mx-auto mb-4" />
                <p className="text-muted-foreground">No products yet. Add your first product!</p>
              </div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6" data-testid="vendor-products-list">
                {products.map((product) => (
                  <div key={product.id} className="border border-border rounded-xl p-4" data-testid="vendor-product-card">
                    <div className="aspect-square rounded-lg overflow-hidden bg-muted mb-4">
                      <img
                        src={product.images?.[0] || "https://images.unsplash.com/photo-1564682628523-316b685c5d7b?w=300"}
                        alt={product.name}
                        className="w-full h-full object-cover"
                      />
                    </div>
                    <h3 className="font-semibold text-lg mb-2" data-testid="vendor-product-card-name">{product.name}</h3>
                    <p className="text-muted-foreground text-sm mb-2 line-clamp-2">{product.description}</p>
                    <div className="flex justify-between items-center mb-4">
                      <div>
                        {product.discount_percentage > 0 ? (
                          <div>
                            <span className="text-sm text-muted-foreground line-through mr-2">₹{product.actual_price}</span>
                            <span className="font-heading text-xl font-bold text-primary" data-testid="vendor-product-card-price">₹{product.offer_price}</span>
                            <span className="ml-2 text-xs bg-green-100 text-green-800 px-2 py-1 rounded-full">{product.discount_percentage}% OFF</span>
                          </div>
                        ) : (
                          <span className="font-heading text-xl font-bold text-primary">₹{product.offer_price || product.price}</span>
                        )}
                      </div>
                      <span className="text-sm text-muted-foreground">Stock: {product.stock}</span>
                    </div>
                    <div className="flex items-center justify-between">
                      <span className={`inline-block px-3 py-1 rounded-full text-xs font-medium ${product.status === 'active' ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'}`}>
                        {product.status}
                      </span>
                      <div className="flex space-x-2">
                        <Button
                          onClick={() => handleEditProduct(product)}
                          variant="outline"
                          size="sm"
                          className="rounded-full"
                          data-testid="vendor-product-edit-btn"
                        >
                          <Edit className="w-4 h-4" />
                        </Button>
                        <Button
                          onClick={() => handleDeleteProduct(product.id)}
                          variant="outline"
                          size="sm"
                          className="rounded-full text-red-500 hover:text-red-600 hover:bg-red-50"
                          data-testid="vendor-product-delete-btn"
                        >
                          <Trash className="w-4 h-4" />
                        </Button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Orders */}
          <div className="bg-card rounded-2xl p-6 shadow-soft" data-testid="vendor-orders-section">
            <h2 className="font-heading text-2xl font-bold mb-6">Orders to Fulfill</h2>
            {vendorOrders.length === 0 ? (
              <div className="text-center py-12" data-testid="vendor-orders-empty">
                <Package className="w-16 h-16 text-muted-foreground mx-auto mb-4" />
                <p className="text-muted-foreground">No orders yet</p>
              </div>
            ) : (
              <div className="space-y-4" data-testid="vendor-orders-list">
                {vendorOrders.map((order) => {
                  const VENDOR_STATUSES = ["pending", "processing", "shipped", "delivered"];
                  const statusColor = {
                    pending: "bg-yellow-100 text-yellow-800",
                    processing: "bg-blue-100 text-blue-800",
                    shipped: "bg-indigo-100 text-indigo-800",
                    delivered: "bg-green-100 text-green-800",
                    fulfilled: "bg-green-100 text-green-800",
                  };
                  const addr = order.shipping_address;
                  return (
                    <div key={order.id} className="border border-border rounded-xl p-4" data-testid="vendor-order-card">
                      <div className="flex justify-between items-start mb-3">
                        <div>
                          <p className="font-semibold text-sm" data-testid="vendor-order-id">Vendor Order #{order.id.slice(0, 8)}</p>
                          <p className="text-xs text-muted-foreground">Main Order: {order.order_id.slice(0, 8)}</p>
                        </div>
                        <span className={`px-3 py-1 rounded-full text-xs font-medium ${statusColor[order.status] || "bg-gray-100 text-gray-800"}`} data-testid="vendor-order-status">
                          {order.status}
                        </span>
                      </div>

                      {/* Customer & shipping details */}
                      {(order.customer || addr) && (
                        <div className="bg-secondary/40 rounded-lg p-3 mb-3 text-sm" data-testid="vendor-order-customer">
                          <p className="font-semibold">{order.customer?.name || "Customer"}</p>
                          {order.customer?.phone && <p className="text-muted-foreground">{order.customer.phone}</p>}
                          {addr && (
                            <p className="text-muted-foreground mt-1">
                              {[addr.address_line1, addr.address_line2, addr.city, addr.state, addr.pincode].filter(Boolean).join(", ")}
                            </p>
                          )}
                        </div>
                      )}

                      <div className="space-y-2 mb-3">
                        {order.items.map((item, idx) => (
                          <div key={idx} className="flex justify-between text-sm">
                            <span className="text-muted-foreground">
                              {item.product_name} x {item.quantity}
                            </span>
                            <span className="font-semibold">₹{(item.price * item.quantity).toFixed(2)}</span>
                          </div>
                        ))}
                        <div className="flex justify-between text-sm pt-2 border-t border-border">
                          <span className="font-semibold">Subtotal</span>
                          <span className="font-bold text-primary" data-testid="vendor-order-subtotal">₹{(order.subtotal || 0).toFixed(2)}</span>
                        </div>
                      </div>

                      <div className="flex flex-wrap items-center gap-2 pt-2 border-t border-border">
                        <span className="text-xs text-muted-foreground mr-1">Update status:</span>
                        {VENDOR_STATUSES.map((s) => (
                          <button
                            key={s}
                            onClick={() => handleUpdateVendorOrderStatus(order.id, s)}
                            disabled={order.status === s}
                            className={`px-3 py-1 rounded-full text-xs font-medium border transition-colors capitalize disabled:opacity-100 ${
                              order.status === s
                                ? "bg-primary text-primary-foreground border-primary"
                                : "bg-background border-border hover:border-primary"
                            }`}
                            data-testid={`vendor-order-status-${s}`}
                          >
                            {s}
                          </button>
                        ))}
                        <Button
                          onClick={() => handlePrintLabel(order)}
                          variant="outline"
                          size="sm"
                          className="rounded-full ml-auto"
                          data-testid="vendor-print-label-btn"
                        >
                          <Printer className="w-4 h-4 mr-1" /> Print Label
                        </Button>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </div>
      </div>

      <Footer />
    </div>
  );
}
